/**
 * Which malware engine produced this verdict.
 *
 * The scanner ships in two tiers and they are MUTUALLY EXCLUSIVE: free runs ClamAV, premium
 * runs Bitdefender *instead of* ClamAV. Never both. A report that does not say which one ran
 * cannot be defended when a customer challenges a finding, because the answer to "why did you
 * flag this" is different for each engine.
 *
 * ── Two sources, in this order ───────────────────────────────────────────────────────────────
 *
 * 1. The `engines` map — whichever of ClamAV / Bitdefender is `complete`.
 * 2. The `scanner` provenance block's `malware_engine`.
 *
 * The second is needed because of a real gap: the engine summary was built from a fixed
 * whitelist that omitted Bitdefender, so a premium scan's `engines` map has ClamAV as
 * `not_applicable` and **no Bitdefender row at all**. Verified on AD2_EC2_Full_20260902123552.
 * Reading only the map would report "not recorded" for a scan whose own provenance block says
 * `"malware_engine": "bitdefender"` in plain text.
 *
 * That is not the same as inferring the tier. Inference would be guessing from findings, the
 * object, or the customer — which is forbidden, and rightly. This is reading a value the
 * scanner wrote down about itself; it is just written in the other half of the report.
 *
 * If neither source says, the answer is "not recorded". Never ClamAV-by-default: defaulting
 * misattributes a premium scan to the free engine, which is exactly the claim a customer would
 * challenge.
 */

export const MALWARE_ENGINES = ['ClamAV', 'Bitdefender'] as const
export type MalwareEngineName = (typeof MALWARE_ENGINES)[number]

export interface MalwareEngineInfo {
    /** The engine that produced the verdict, or null when nothing recorded it. */
    engine: MalwareEngineName | null
    /** Where the answer came from — shown so the reader can weigh it. */
    source: 'engines' | 'scanner' | null
    /** The other engine, when the report explains it was stood down rather than failed. */
    replaced: { name: MalwareEngineName; reason: string } | null
    /** Both engines reported complete. A bug in the scan, surfaced rather than averaged away. */
    conflict: boolean
    /**
     * No malware engine applies to this object — every one present stood down with a reason, and
     * none ran. A network device's configuration has no executable content for a signature engine
     * to match, and configuration analysis replaces it. That is correct, not missing coverage.
     */
    notApplicable: { reason: string } | null
    /** Signature/definition version, when the provenance block was persisted. */
    signature: string | null
    tier: string | null
    imageDigest: string | null
}

function entry(engines: any, name: string): any | null {
    if (!engines || typeof engines !== 'object') return null
    // The map is PascalCase; be forgiving about case without being forgiving about identity.
    const key = Object.keys(engines).find((k) => k.toLowerCase() === name.toLowerCase())
    return key ? engines[key] : null
}

const isComplete = (e: any) => ['complete', 'completed'].includes(String(e?.status || '').toLowerCase())

export function malwareEngine(report: any): MalwareEngineInfo {
    const engines = report?.engines
    const scanner = report?.scanner

    const rows = MALWARE_ENGINES.map((name) => ({ name, e: entry(engines, name) }))
    const completed = rows.filter((r) => isComplete(r.e))

    const tier = scanner?.tier ? String(scanner.tier) : null
    const imageDigest = scanner?.image_digest ? String(scanner.image_digest) : null

    // Rule 2: never render both as active. Two complete malware engines is a fault.
    if (completed.length > 1) {
        return {
            engine: null, source: null, replaced: null, conflict: true, notApplicable: null,
            signature: null, tier, imageDigest,
        }
    }

    const signatureFor = (name: MalwareEngineName): string | null => {
        const v = name === 'ClamAV' ? scanner?.clamav_version : scanner?.bitdefender_db
        return v ? String(v) : null
    }

    // Rule 1: not_applicable is not a failure — it means the other engine replaced it. Carry the
    // reason so the operator is not told coverage was lost when it was not.
    const standDown = rows.find((r) => String(r.e?.status || '').toLowerCase() === 'not_applicable')
    const replaced = standDown?.e?.reason
        ? { name: standDown.name, reason: String(standDown.e.reason) }
        : null

    if (completed.length === 1) {
        const name = completed[0].name
        return { engine: name, source: 'engines', replaced, conflict: false, notApplicable: null, signature: signatureFor(name), tier, imageDigest }
    }

    // Rule 4: no malware engine applies to this object. Decided by STRUCTURE, never by the reason's
    // wording, and it has to be told apart from the premium whitelist gap above, which looks
    // similar — ClamAV not_applicable, Bitdefender absent — but means Bitdefender RAN:
    //   * the engine the scanner declares has its OWN row, and that row stood down; or
    //   * both engines are present and both stood down.
    // An engine that is declared but has no row ran and was not listed; that stays Rule 1/2.
    const declared = String(scanner?.malware_engine || '').toLowerCase()
    const standsDown = (r: { e: any }) => String(r.e?.status || '').toLowerCase() === 'not_applicable'
    const declaredRow = rows.find((r) => r.name.toLowerCase() === declared && r.e)
    const allStoodDown = rows.every((r) => r.e && standsDown(r))
    if ((declaredRow && standsDown(declaredRow)) || allStoodDown) {
        const reason = (declaredRow?.e?.reason) || rows.map((r) => r.e?.reason).find(Boolean)
        return {
            engine: null, source: null, replaced: null, conflict: false,
            notApplicable: { reason: reason ? String(reason) : 'No malware engine applies to this object.' },
            signature: null, tier, imageDigest,
        }
    }

    // Nothing in the engines map completed. Fall back to what the scanner said about itself.
    const match = MALWARE_ENGINES.find((n) => n.toLowerCase() === declared)
    if (match) {
        return { engine: match, source: 'scanner', replaced, conflict: false, notApplicable: null, signature: signatureFor(match), tier, imageDigest }
    }

    // Rule 3: say so. Do not guess.
    return { engine: null, source: null, replaced, conflict: false, notApplicable: null, signature: null, tier, imageDigest }
}
